import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Award } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';

const TrainersPage = () => {
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTrainers = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`${API}/trainers`);
      setTrainers(response.data || []);
    } catch (err) {
      setError('Impossible de charger les formateurs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrainers();
  }, [fetchTrainers]);

  if (error) {
    return (
      <div className="min-h-screen bg-black pt-24 flex items-center justify-center">
        <p className="font-['Barlow'] text-gray-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-24">
      {/* Hero Section */}
      <section className="py-16 border-b border-white/10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="font-['Barlow'] text-[#FACC15] text-sm uppercase tracking-[0.3em] mb-4 block">
              Notre équipe pédagogique
            </span>
            <h1 className="font-['Teko'] text-5xl md:text-7xl text-white uppercase leading-none mb-6">
              NOS FORMATEURS
            </h1>
            <p className="font-['Barlow'] text-gray-400 max-w-2xl mx-auto text-lg">
              Des professionnels expérimentés pour vous transmettre leur savoir-faire et leur passion du métier de cascadeur.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Trainers Grid */}
      <section className="py-24">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : trainers.length === 0 ? (
            <div className="text-center py-20">
              <Award size={64} className="mx-auto text-gray-600 mb-6" />
              <p className="font-['Barlow'] text-gray-500 text-xl">
                Les formateurs seront bientôt disponibles.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {trainers.map((trainer, index) => (
                <motion.div
                  key={trainer.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="group bg-[#09090b] border border-white/10 hover:border-[#FACC15]/30 transition-all duration-300"
                >
                  {/* Photo */}
                  <div className="aspect-[9/16] w-full overflow-hidden">
                    {trainer.photo_url ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${trainer.photo_url}`}
                        alt={trainer.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#FACC15]/20 to-[#FACC15]/5">
                        <span className="font-['Teko'] text-6xl text-[#FACC15]">
                          {trainer.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="p-6">
                    <h3 className="font-['Teko'] text-2xl text-white uppercase">
                      {trainer.name}
                    </h3>
                    <p className="font-['Barlow'] text-[#FACC15] text-sm uppercase tracking-wider mt-1">
                      {trainer.role}
                    </p>
                    {trainer.specialty && (
                      <p className="font-['Barlow'] text-gray-400 text-sm mt-2">
                        {trainer.specialty}
                      </p>
                    )}
                    {trainer.bio && (
                      <p className="font-['Barlow'] text-gray-300 text-sm mt-4 leading-relaxed">
                        {trainer.bio}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default TrainersPage;
